package com.zq.webproject.ui.withdraw

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.CoroutineScope
import androidx.compose.runtime.rememberCoroutineScope
import org.jetbrains.compose.resources.Font
import com.zq.webproject.utils.TokenManager

import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import com.zq.webproject.model.BankCard
import com.zq.webproject.model.WithdrawRecord
import com.zq.webproject.utils.ToastManager
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song
// 统一字体
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily(Font(Res.font.font_song))

/**
 * 提现界面
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun WithdrawScreen(
    balance: Double?,
    onBackClick: () -> Unit,
    onAddBankCardClick: () -> Unit = {},
    onWithdrawRecordClick: () -> Unit = {}
) {
    println("WithdrawScreen - 渲染提现界面") // 日志输出

    // 创建ViewModel实例
    val viewModel = remember { WithdrawViewModel() }

    // 提现金额输入
    var withdrawAmount by remember { mutableStateOf("") }

    // 选中的银行卡
    var selectedBankCard by remember { mutableStateOf<BankCard?>(null) }

    // 是否显示银行卡选择对话框
    var showBankCardDialog by remember { mutableStateOf(false) }

    // 错误提示
    var errorTip by remember { mutableStateOf<String?>(null) }

    // 成功提示
    var successTip by remember { mutableStateOf<String?>(null) }

    // Snackbar状态
    val snackbarHostState = remember { SnackbarHostState() }
    val coroutineScope = rememberCoroutineScope()

    // 初始化ToastManager
    LaunchedEffect(Unit) {
        ToastManager.init(snackbarHostState, coroutineScope)
        println("WithdrawScreen - ToastManager初始化完成") // 日志输出
    }

    // 错误提示自动弹出Snackbar
    LaunchedEffect(errorTip) {
        if (errorTip != null) {
            println("WithdrawScreen - 调用ToastManager显示错误: $errorTip") // 日志输出
            ToastManager.showError(errorTip!!)
        }
    }

    // 成功提示自动弹出Snackbar
    LaunchedEffect(successTip) {
        if (successTip != null) {
            println("WithdrawScreen - 调用ToastManager显示成功: $successTip") // 日志输出
            ToastManager.showSuccess(successTip!!)
            successTip = null
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "提现",
                        fontFamily = CaiFont,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = {
                        println("WithdrawScreen - 点击返回按钮") // 日志输出
                        onBackClick()
                    }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "返回"
                        )
                    }
                },
                actions = {
                    IconButton(onClick = {
                        println("WithdrawScreen - 点击银行卡按钮") // 日志输出
                        onAddBankCardClick()
                    }) {
                        Icon(
                            imageVector = Icons.Default.CreditCard,
                            contentDescription = "银行卡",
                            tint = Color.White
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        },
        snackbarHost = {
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                SnackbarHost(hostState = snackbarHostState)
            }
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF5F5F5))
                .verticalScroll(rememberScrollState())
        ) {
            // 账户余额卡片
            BalanceCard(
                balance = viewModel.balance ?: 0.0,
                onRecordClick = onWithdrawRecordClick
            )

            Spacer(modifier = Modifier.height(12.dp))

            // 银行卡选择卡片
            BankCardSelectionCard(
                selectedBankCard = selectedBankCard,
                onClick = {
                    println("WithdrawScreen - 点击选择银行卡") // 日志输出
                    showBankCardDialog = true
                }
            )

            Spacer(modifier = Modifier.height(12.dp))

            // 提现金额输入卡片
            WithdrawAmountCard(
                withdrawAmount = withdrawAmount,
                onAmountChange = { withdrawAmount = it },
                selectedBankCard = selectedBankCard,
                onSubmit = {
                    if (selectedBankCard == null) {
                        errorTip = "请先选择银行卡"
                        successTip = null
                        println("WithdrawScreen - 提现失败：未选择银行卡") // 日志输出
                        return@WithdrawAmountCard
                    }

                    println("WithdrawScreen - 提交提现申请，金额: $withdrawAmount, 银行卡: ${selectedBankCard!!.bankName}") // 日志输出
                    viewModel.applyWithdraw(
                        amount = withdrawAmount,
                        onSuccess = {
                            successTip = "提现申请提交成功，请等待审核"
                            errorTip = null
                            withdrawAmount = ""  // 清空输入
                            selectedBankCard = null  // 清空选择
                            println("WithdrawScreen - 提现申请成功") // 日志输出
                        },
                        onError = { error ->
                            errorTip = error
                            successTip = null
                            println("WithdrawScreen - 提现申请失败: $error") // 日志输出
                        }
                    )
                },
                isLoading = viewModel.isLoading
            )

            Spacer(modifier = Modifier.height(12.dp))

            // 提示信息卡片
            TipCard()

            Spacer(modifier = Modifier.height(12.dp))

            // 错误提示
            if (errorTip != null) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFFFEBEE)),
                    elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            imageVector = Icons.Default.Error,
                            contentDescription = "错误",
                            tint = Color.Red,
                            modifier = Modifier.size(24.dp)
                        )
                        Spacer(modifier = Modifier.width(12.dp))
                        Text(
                            text = errorTip!!,
                            color = Color.Red,
                            fontSize = 15.sp,
                            fontWeight = FontWeight.Bold,
                            fontFamily = CaiFont
                        )
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            // 成功提示
            if (successTip != null) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                    elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            imageVector = Icons.Default.CheckCircle,
                            contentDescription = "成功",
                            tint = Color(0xFF4CAF50),
                            modifier = Modifier.size(24.dp)
                        )
                        Spacer(modifier = Modifier.width(12.dp))
                        Text(
                            text = successTip!!,
                            color = Color(0xFF4CAF50),
                            fontSize = 15.sp,
                            fontWeight = FontWeight.Bold,
                            fontFamily = CaiFont
                        )
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            Spacer(modifier = Modifier.height(12.dp))
        }
    }

    // 银行卡选择对话框
    if (showBankCardDialog) {
        // TODO: 替换为真实的银行卡列表
        val mockBankCards = listOf(
            BankCard(
                id = 1,
                userId = 1,
                cardNumber = "6222021234567890",
                cardHolderName = "张三",
                expirationDate = "12/25",
                cvv = "123",
                bankName = "中国工商银行"
            ),
            BankCard(
                id = 2,
                userId = 1,
                cardNumber = "6228481234567890",
                cardHolderName = "张三",
                expirationDate = "11/26",
                cvv = "456",
                bankName = "中国农业银行"
            )
        )

        BankCardSelectionDialog(
            bankCards = mockBankCards,
            onDismiss = {
                println("WithdrawScreen - 关闭银行卡选择对话框") // 日志输出
                showBankCardDialog = false
            },
            onSelect = { bankCard ->
                selectedBankCard = bankCard
                showBankCardDialog = false
                println("WithdrawScreen - 已选择银行卡: ${bankCard.bankName}") // 日志输出
            }
        )
    }
}

/**
 * 账户余额卡片
 */
@Composable
fun BalanceCard(
    balance: Double,
    onRecordClick: () -> Unit
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column {
                Text(
                    text = "账户余额",
                    fontSize = 14.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = "￥$balance",
                    fontSize = 28.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFFFF6F00),
                    fontFamily = CaiFont
                )
            }

            OutlinedButton(
                onClick = {
                    println("WithdrawScreen - 点击提现记录按钮") // 日志输出
                    onRecordClick()
                },
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = Color(0xFF1E88E5)
                ),
                shape = RoundedCornerShape(20.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Receipt,
                    contentDescription = "记录",
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(4.dp))
                Text(
                    text = "提现记录",
                    fontFamily = CaiFont,
                    fontSize = 14.sp
                )
            }
        }
    }
}
/**
 * 提现金额输入卡片
 */
@Composable
fun WithdrawAmountCard(
    withdrawAmount: String,
    onAmountChange: (String) -> Unit,
    selectedBankCard: BankCard?,
    onSubmit: () -> Unit,
    isLoading: Boolean
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            Text(
                text = "申请提现金额",
                fontSize = 16.sp,
                fontWeight = FontWeight.Bold,
                fontFamily = CaiFont
            )

            Spacer(modifier = Modifier.height(12.dp))

            OutlinedTextField(
                value = withdrawAmount,
                onValueChange = { newValue ->
                    // 只允许输入数字
                    if (newValue.isEmpty() || newValue.all { it.isDigit() }) {
                        println("WithdrawScreen - 输入金额: $newValue") // 日志输出
                        onAmountChange(newValue)
                    }
                },
                label = {
                    Text(
                        text = "请输入提现金额",
                        fontFamily = CaiFont
                    )
                },
                prefix = {
                    Text(
                        text = "￥",
                        fontFamily = CaiFont
                    )
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = Color(0xFF1E88E5),
                    focusedLabelColor = Color(0xFF1E88E5)
                )
            )

            // 红色提示文字
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = "提现金额必须是100的倍数",
                fontSize = 12.sp,
                color = Color.Red,
                fontFamily = CaiFont
            )

            Spacer(modifier = Modifier.height(16.dp))

            Button(
                onClick = {
                    println("WithdrawAmountCard - 点击申请提现按钮") // 日志输出
                    onSubmit()
                },
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFF1E88E5)
                ),
                enabled = withdrawAmount.isNotEmpty() && selectedBankCard != null && !isLoading
            ) {
                if (isLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                }
                Text(
                    text = if (isLoading) "处理中..." else "申请提现",
                    fontFamily = CaiFont,
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp
                )
            }
        }
    }
}

/**
 * 提示信息卡片
 */
@Composable
fun TipCard() {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF3E0)),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    imageVector = Icons.Default.Info,
                    contentDescription = "提示",
                    tint = Color(0xFFFF6F00),
                    modifier = Modifier.size(20.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "温馨提示",
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFFFF6F00),
                    fontFamily = CaiFont
                )
            }

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = "• 提现金额必须是100的倍数",
                fontSize = 13.sp,
                color = Color(0xFF5D4037),
                fontFamily = CaiFont
            )

            Spacer(modifier = Modifier.height(4.dp))

            Text(
                text = "• 每月提现金额大于5000需要缴纳个人所得税",
                fontSize = 13.sp,
                color = Color(0xFF5D4037),
                fontFamily = CaiFont
            )

            Spacer(modifier = Modifier.height(4.dp))

            Text(
                text = "• 提现申请提交后将在1-3个工作日内处理",
                fontSize = 13.sp,
                color = Color(0xFF5D4037),
                fontFamily = CaiFont
            )
        }
    }
}

/**
 * 提现记录列表
 */
@Composable
fun WithdrawRecordList(records: List<WithdrawRecord>) {
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(vertical = 8.dp)
    ) {
        items(records) { record ->
            WithdrawRecordItem(record = record)
        }
    }
}
/**
 * 提现记录单项
 */
@Composable
fun WithdrawRecordItem(record: WithdrawRecord) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 6.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "提现金额",
                    fontSize = 14.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )
                Text(
                    text = "￥${record.amount}",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFFFF6F00),
                    fontFamily = CaiFont
                )
            }

            Spacer(modifier = Modifier.height(12.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = "银行卡号",
                    fontSize = 13.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )
                Text(
                    text = record.cardNumber ?: "未设置",
                    fontSize = 13.sp,
                    color = Color.Black,
                    fontFamily = CaiFont
                )
            }

            Spacer(modifier = Modifier.height(8.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = "申请时间",
                    fontSize = 13.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )
                Text(
                    text = formatWithdrawTime(record.createdAt),
                    fontSize = 13.sp,
                    color = Color.Black,
                    fontFamily = CaiFont
                )
            }

            // 如果有处理时间，显示处理时间
            if (!record.handleTime.isNullOrEmpty()) {
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = "处理时间",
                        fontSize = 13.sp,
                        color = Color.Gray,
                        fontFamily = CaiFont
                    )
                    Text(
                        text = formatWithdrawTime(record.handleTime!!),
                        fontSize = 13.sp,
                        color = Color.Black,
                        fontFamily = CaiFont
                    )
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "状态",
                    fontSize = 13.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )

                // 状态标签（使用接口返回的statusText）
                val statusText = record.statusText ?: when (record.status) {
                    0 -> "待审核"
                    1 -> "成功"
                    2 -> "已拒绝"
                    else -> "未知"
                }
                val statusColor = when (record.status) {
                    0 -> Color(0xFFFF9800)  // 待审核-橙色
                    1 -> Color(0xFF4CAF50)  // 成功-绿色
                    2 -> Color(0xFFF44336)  // 已拒绝-红色
                    else -> Color.Gray
                }

                Surface(
                    color = statusColor.copy(alpha = 0.1f),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Text(
                        text = statusText,
                        fontSize = 12.sp,
                        color = statusColor,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont,
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp)
                    )
                }
            }
        }
    }
}

/**
 * 空状态组件
 */
@Composable
fun EmptyState(message: String) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Icon(
                imageVector = Icons.Default.Receipt,
                contentDescription = "空状态",
                modifier = Modifier.size(64.dp),
                tint = Color.Gray.copy(alpha = 0.3f)
            )
            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = message,
                fontSize = 14.sp,
                color = Color.Gray,
                fontFamily = CaiFont
            )
        }
    }
}

/**
 * 银行卡选择卡片
 */
@Composable
fun BankCardSelectionCard(
    selectedBankCard: BankCard?,
    onClick: () -> Unit
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            Text(
                text = "提现银行卡",
                fontSize = 14.sp,
                color = Color.Gray,
                fontFamily = CaiFont
            )

            Spacer(modifier = Modifier.height(8.dp))

            // 选择银行卡按钮
            OutlinedButton(
                onClick = onClick,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = if (selectedBankCard != null) Color(0xFF1E88E5) else Color.Gray
                )
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (selectedBankCard != null) {
                        Column(
                            modifier = Modifier.weight(1f)
                        ) {
                            Text(
                                text = selectedBankCard.bankName,
                                fontSize = 15.sp,
                                fontWeight = FontWeight.Bold,
                                fontFamily = CaiFont
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            Text(
                                text = "尾号 ${selectedBankCard.cardNumber.takeLast(4)}",
                                fontSize = 13.sp,
                                fontFamily = CaiFont
                            )
                        }
                    } else {
                        Text(
                            text = "请选择银行卡",
                            fontSize = 15.sp,
                            fontFamily = CaiFont,
                            modifier = Modifier.weight(1f)
                        )
                    }

                    Icon(
                        imageVector = Icons.Default.KeyboardArrowRight,
                        contentDescription = "选择"
                    )
                }
            }
        }
    }
}
/**
 * 银行卡选择对话框
 */
@Composable
fun BankCardSelectionDialog(
    bankCards: List<BankCard>,
    onDismiss: () -> Unit,
    onSelect: (BankCard) -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = "选择银行卡",
                fontFamily = CaiFont,
                fontWeight = FontWeight.Bold
            )
        },
        text = {
            if (bankCards.isEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 32.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = "暂无银行卡，请先添加银行卡",
                        fontSize = 14.sp,
                        color = Color.Gray,
                        fontFamily = CaiFont
                    )
                }
            } else {
                LazyColumn {
                    items(bankCards) { bankCard ->
                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 4.dp),
                            colors = CardDefaults.cardColors(containerColor = Color(0xFFF5F5F5)),
                            onClick = {
                                println("WithdrawScreen - 选中银行卡: ${bankCard.bankName} 尾号${bankCard.cardNumber.takeLast(4)}") // 日志输出
                                onSelect(bankCard)
                            }
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    imageVector = Icons.Default.CreditCard,
                                    contentDescription = "银行卡",
                                    tint = Color(0xFF1E88E5),
                                    modifier = Modifier.size(32.dp)
                                )

                                Spacer(modifier = Modifier.width(12.dp))

                                Column(
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Text(
                                        text = bankCard.bankName,
                                        fontSize = 15.sp,
                                        fontWeight = FontWeight.Bold,
                                        fontFamily = CaiFont
                                    )
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text(
                                        text = "**** **** **** ${bankCard.cardNumber.takeLast(4)}",
                                        fontSize = 13.sp,
                                        color = Color.Gray,
                                        fontFamily = CaiFont
                                    )
                                }
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = onDismiss
            ) {
                Text(
                    text = "关闭",
                    fontFamily = CaiFont
                )
            }
        }
    )
}

/**
 * 格式化提现时间
 * 将 ISO 8601 格式转换为友好的显示格式
 * 例如："2025-12-30T02:45:31.000+00:00" -> "2025-12-30 02:45"
 */
fun formatWithdrawTime(dateString: String): String {
    return try {
        val parts = dateString.split("T")
        if (parts.size >= 2) {
            val date = parts[0]
            val time = parts[1].substringBefore(".")
            "$date ${time.substring(0, 5)}"  // 只取小时和分钟
        } else {
            dateString
        }
    } catch (e: Exception) {
        println("WithdrawScreen - 日期格式化失败: ${e.message}") // 日志输出
        dateString
    }
}
